import {
  Aperture, Asterisk, Bot, FileText, HardDrive, Hash, Mail, MessageSquareText, Mic, MousePointer2, Paperclip, Pencil,
  PenLine, Terminal, Upload, type LucideIcon,
} from 'lucide-react';
import type { FeedSourceType } from '../../lib/api';
import { cn } from '../../lib/utils';

/**
 * One colour and one glyph per kind of source, so "what came from where" reads at a glance down a
 * list of runs rather than by reading each label. The colours lean on each product's own (Slack's
 * aubergine, Gmail's red, Drive's blue, Granola's green) because that is what people already
 * recognise; the kinds people produce by hand — a paste, an edit, feedback — get calmer tones so the
 * connectors stand out. Every chip carries its name as a tooltip, since colour alone is not a label.
 */
export const SOURCE_STYLES: Record<FeedSourceType, { label: string; plural: string; icon: LucideIcon; tone: string }> = {
  SLACK: { label: 'Slack', plural: 'Slack messages', icon: Hash, tone: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300' },
  GRANOLA: { label: 'Granola', plural: 'Granola meetings', icon: Mic, tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300' },
  EMAIL: { label: 'Email', plural: 'emails', icon: Mail, tone: 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-300' },
  DRIVE_FILE: { label: 'Google Drive', plural: 'Drive files', icon: HardDrive, tone: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300' },
  // A mail attachment or a document linked in Slack: it came *with* something else.
  LINKED_DOCUMENT: { label: 'Attachment', plural: 'attachments', icon: Paperclip, tone: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' },
  FILE_UPLOAD: { label: 'Upload', plural: 'uploads', icon: Upload, tone: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300' },
  MANUAL: { label: 'Manual', plural: 'manual notes', icon: PenLine, tone: 'bg-slate-200 text-slate-700 dark:bg-slate-500/25 dark:text-slate-200' },
  MCP: { label: 'AI agent (MCP)', plural: 'agent notes', icon: Bot, tone: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300' },
  USER_EDIT: { label: 'Hand edit', plural: 'hand edits', icon: Pencil, tone: 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300' },
  USER_FEEDBACK: { label: 'Feedback', plural: 'feedback replies', icon: MessageSquareText, tone: 'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300' },
  AUTOMATION_EDIT: { label: 'Brief edit', plural: 'brief edits', icon: FileText, tone: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300' },
  CLI_IMPORT: { label: 'CLI import', plural: 'CLI imports', icon: Terminal, tone: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-500/25 dark:text-zinc-200' },
};

const FALLBACK = { label: 'Source', plural: 'items', icon: FileText, tone: 'bg-surface-muted text-ink-muted' };

/**
 * An MCP arrival drawn as the assistant that wrote it, from the caller's user agent (`mcpClientOf` in
 * shared-types): Claude in its terracotta, ChatGPT in OpenAI's black and white. An unknown client keeps
 * the generic MCP chip.
 */
const MCP_CLIENT_STYLES: Record<string, { label: string; plural: string; icon: LucideIcon; tone: string }> = {
  claude: { label: 'Claude (MCP)', plural: 'notes from Claude', icon: Asterisk, tone: 'bg-[#F6E3DA] text-[#C15F3C] dark:bg-[#C15F3C]/25 dark:text-[#F2A788]' },
  chatgpt: { label: 'ChatGPT (MCP)', plural: 'notes from ChatGPT', icon: Aperture, tone: 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900' },
  cursor: { label: 'Cursor (MCP)', plural: 'notes from Cursor', icon: MousePointer2, tone: 'bg-neutral-200 text-neutral-800 dark:bg-neutral-500/25 dark:text-neutral-100' },
};

export function sourceStyle(sourceType: string, client?: string | null) {
  if (sourceType === 'MCP' && client && MCP_CLIENT_STYLES[client]) return MCP_CLIENT_STYLES[client]!;
  return SOURCE_STYLES[sourceType as FeedSourceType] ?? FALLBACK;
}

/** A small coloured square with the source's glyph — and, when given, how many items of it. */
export function SourceIcon({
  sourceType,
  client,
  count,
  size = 'sm',
  className,
}: {
  sourceType: string;
  /** For MCP: which assistant wrote it. */
  client?: string | null;
  count?: number;
  size?: 'xs' | 'sm';
  className?: string;
}) {
  const style = sourceStyle(sourceType, client);
  const Icon = style.icon;
  const showCount = count !== undefined && count > 1;
  const title = count !== undefined ? `${count} ${count === 1 ? style.label : style.plural}` : style.label;
  return (
    <span
      title={title}
      aria-label={title}
      className={cn(
        'inline-flex flex-shrink-0 items-center justify-center gap-0.5 rounded-md font-semibold tabular-nums',
        size === 'xs' ? 'h-4 min-w-4 px-0.5 text-[9px]' : 'h-5 min-w-5 px-1 text-[10px]',
        style.tone,
        className,
      )}
    >
      <Icon size={size === 'xs' ? 10 : 11} strokeWidth={2.25} />
      {showCount && <span>{count}</span>}
    </span>
  );
}
